import React, { useState } from 'react';
import { Shield, Eye, EyeOff, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useSuperUser } from '../context/SuperUserContext';
import { toast } from 'sonner';

export function SuperUserDialog({ 
  open, 
  onOpenChange, 
  onSuccess, 
  title = "Acción Restringida",
  description = "Esta acción requiere autorización de super usuario."
}) {
  const [code, setCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { verifyCode, isSuperUser } = useSuperUser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Pequeño delay para feedback visual
    await new Promise(resolve => setTimeout(resolve, 300));

    if (verifyCode(code)) {
      toast.success('Código verificado correctamente');
      setCode('');
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } else {
      toast.error('Código incorrecto');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setCode('');
    onOpenChange(false);
  };

  // Si ya es super usuario activo, ejecutar directamente
  if (isSuperUser && open) {
    setTimeout(() => {
      onOpenChange(false);
      if (onSuccess) onSuccess();
    }, 0);
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Shield className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="super-code" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Código de Super Usuario
            </Label>
            <div className="relative">
              <Input
                id="super-code"
                type={showCode ? "text" : "password"}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Ingresa el código maestro"
                className="pr-10 font-mono tracking-wider"
                autoFocus
                data-testid="super-user-code-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowCode(!showCode)}
              >
                {showCode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={!code || loading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="verify-super-user-btn"
            >
              {loading ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Verificar
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
